import {useEffect} from 'react'
import isEqual from 'lodash/isEqual'

type UseSyncCookiesFromUrlOptions<TCookie> = {
    isParamsInUrl: boolean
    currentCookieValues?: TCookie | null
    getCookieValuesFromStorage: () => TCookie | undefined
    paramsFromUrl: TCookie
    formatCookie: (values: TCookie) => string | object
    setCookiesFromUrl: () => void
    setCookieValuesState: (values: TCookie | undefined) => void
    setContextState?: (values: TCookie) => any
    deepCompare?: boolean
}

export const useSyncCookiesFromUrl = <TCookie>({
    isParamsInUrl,
    currentCookieValues,
    getCookieValuesFromStorage,
    paramsFromUrl,
    formatCookie,
    setCookiesFromUrl,
    setCookieValuesState,
    setContextState,
    deepCompare = false
}: UseSyncCookiesFromUrlOptions<TCookie>) => {
    useEffect(() => {
        if (!isParamsInUrl) return

        const oldFormatted = currentCookieValues ? formatCookie(currentCookieValues) : null
        const newFormatted = formatCookie(paramsFromUrl)
        const isSame = deepCompare
            ? isEqual(oldFormatted, newFormatted)
            : oldFormatted === newFormatted

        if (isSame) return

        setCookiesFromUrl()

        const timeout = setTimeout(() => {
            const newValues = getCookieValuesFromStorage()
            setCookieValuesState(newValues)
            if (setContextState && newValues) {
                setContextState(newValues)
            }
        }, 100)

        return () => clearTimeout(timeout)
    }, [
        isParamsInUrl,
        currentCookieValues,
        paramsFromUrl,
        setCookiesFromUrl,
        getCookieValuesFromStorage,
        setContextState,
        formatCookie,
        setCookieValuesState,
        deepCompare
    ])
}
